import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "@/components/ui/sonner";
import { Target, Plus } from "lucide-react";
import GoalCard from "@/components/goals/GoalCard";
import CreateGoalModal from "@/components/goals/CreateGoalModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = ["all", "active", "completed"];

export default function GoalsPage({ isMobile, onGamificationData }) {
  const { token } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchGoals = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/goals`, { headers: authHeaders });
      setGoals(data);
    } catch {
      toast.error("Failed to load goals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGoals(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoalSaved = (savedGoal) => {
    setGoals((prev) => {
      const idx = prev.findIndex((g) => g.id === savedGoal.id);
      if (idx >= 0) {
        const next = [...prev]; next[idx] = savedGoal; return next;
      }
      return [savedGoal, ...prev];
    });
    if (savedGoal.new_achievements?.length > 0 && onGamificationData) {
      onGamificationData({ xp_earned: 0, new_achievements: savedGoal.new_achievements });
    }
  };

  const handleGoalUpdated = (updatedGoal) => {
    setGoals((prev) => prev.map((g) => g.id === updatedGoal.id ? updatedGoal : g));
  };

  const handleArchive = async (goalId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/goals/${goalId}/archive`, {}, { headers: authHeaders });
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      toast.success("Goal archived");
    } catch {
      toast.error("Failed to archive goal");
    }
  };

  const handleDelete = async (goalId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/goals/${goalId}`, { headers: authHeaders });
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      toast.success("Goal deleted");
    } catch {
      toast.error("Failed to delete goal");
    }
  };

  const handleEdit = (goal) => { setEditingGoal(goal); setShowModal(true); };

  const filtered = goals.filter((g) => {
    if (tab === "active")    return g.status === "active";
    if (tab === "completed") return g.status === "completed";
    return true;
  });

  const tabCount = (t) => {
    if (t === "active")    return goals.filter((g) => g.status === "active").length;
    if (t === "completed") return goals.filter((g) => g.status === "completed").length;
    return goals.length;
  };

  return (
    <div data-testid="goals-page" style={{ width: "100%", maxWidth: "800px" }}>
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: isMobile ? "1.5rem" : "1.75rem", fontWeight: 800, color: "#1A4D2E", margin: 0, textShadow: "0 1px 3px rgba(255,255,255,0.9)" }}>
            My Goals
          </h1>
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.875rem", margin: "0.2rem 0 0" }}>
            Track your big-picture outcomes
          </p>
        </div>
        <button
          data-testid="new-goal-button"
          onClick={() => { setEditingGoal(null); setShowModal(true); }}
          style={{
            display: "flex", alignItems: "center", gap: "0.4rem",
            background: "#1A4D2E", color: "#fff", border: "none",
            borderRadius: "2rem", padding: "0.6rem 1.25rem",
            fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "0.875rem",
            cursor: "pointer", boxShadow: "0 2px 12px rgba(26,77,46,0.3)",
          }}
        >
          <Plus size={16} /> New Goal
        </button>
      </div>

      {/* ── Tab filters ── */}
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1.25rem", background: "rgba(255,255,255,0.5)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.6)", borderRadius: "2rem", padding: "0.3rem", width: "fit-content" }}>
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            style={{
              padding: "0.4rem 1rem", borderRadius: "2rem", border: "none",
              background: tab === t ? "#1A4D2E" : "transparent",
              color: tab === t ? "#fff" : "#6b7280",
              fontFamily: "'Manrope', sans-serif", fontSize: "0.82rem", fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
              display: "flex", alignItems: "center", gap: "0.3rem",
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            <span style={{
              background: tab === t ? "rgba(255,255,255,0.25)" : "rgba(26,77,46,0.08)",
              borderRadius: "2rem", padding: "0.05rem 0.4rem",
              fontSize: "0.7rem", minWidth: "18px", textAlign: "center",
            }}>
              {tabCount(t)}
            </span>
          </button>
        ))}
      </div>

      {/* ── Goals list ── */}
      {loading ? (
        <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.9rem" }}>Loading goals...</p>
      ) : filtered.length === 0 ? (
        <div style={{
          background: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.7)", borderRadius: "0.875rem",
          padding: "3rem 2rem", textAlign: "center",
        }}>
          <Target size={40} color="rgba(26,77,46,0.2)" style={{ marginBottom: "0.75rem" }} />
          <p style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.95rem", margin: "0 0 1rem" }}>
            {tab === "completed" ? "No completed goals yet — keep going!" : "No goals yet. Set your first goal to get started."}
          </p>
          {tab !== "completed" && (
            <button
              onClick={() => { setEditingGoal(null); setShowModal(true); }}
              style={{
                background: "#1A4D2E", color: "#fff", border: "none", borderRadius: "2rem",
                padding: "0.6rem 1.5rem", fontFamily: "'Manrope', sans-serif", fontWeight: 700,
                fontSize: "0.875rem", cursor: "pointer",
              }}
            >
              Create Your First Goal
            </button>
          )}
        </div>
      ) : (
        filtered.map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            onGamificationData={onGamificationData}
            onEdit={handleEdit}
            onArchive={handleArchive}
            onDelete={handleDelete}
            onGoalUpdated={handleGoalUpdated}
          />
        ))
      )}

      {/* ── Modal ── */}
      {showModal && (
        <CreateGoalModal
          goal={editingGoal}
          onClose={() => { setShowModal(false); setEditingGoal(null); }}
          onSaved={handleGoalSaved}
        />
      )}
    </div>
  );
}
